const SUPPORTED_IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.webp', '.avif']);
const SUPPORTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/avif']);

export const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/webp,image/avif';
export const IMAGE_TYPE_ERROR = 'That file type isn\'t supported. Please use a JPEG, PNG, WebP, or AVIF image.';
export const IMAGE_UPLOAD_MAX_BYTES = 10_000_000;

interface ImageFileValidationOptions {
    invalidImageMessage?: string;
    maxBytes?: number;
}

function formatMegabytes(bytes: number): string {
    return (bytes / 1024 / 1024).toFixed(bytes % (1024 * 1024) === 0 ? 0 : 1);
}

function getFileExtension(file: File): string {
    const name = file.name.toLowerCase();
    const dotIndex = name.lastIndexOf('.');
    return dotIndex >= 0 ? name.slice(dotIndex) : '';
}

export function isSupportedImageFile(file: File): boolean {
    return SUPPORTED_IMAGE_TYPES.has(file.type) || SUPPORTED_IMAGE_EXTENSIONS.has(getFileExtension(file));
}

async function canDecodeImage(file: File): Promise<boolean> {
    const imageUrl = URL.createObjectURL(file);
    try {
        const image = new Image();
        const loaded = new Promise<boolean>((resolve) => {
            image.onload = () => resolve(image.naturalWidth > 0 && image.naturalHeight > 0);
            image.onerror = () => resolve(false);
        });
        image.src = imageUrl;
        return await loaded;
    } finally {
        URL.revokeObjectURL(imageUrl);
    }
}

export async function validateImageFile(
    file: File,
    options: ImageFileValidationOptions = {}
): Promise<string | null> {
    const maxBytes = options.maxBytes ?? IMAGE_UPLOAD_MAX_BYTES;
    if (file.size <= 0) {
        return 'This image appears to be empty. Please choose a different file.';
    }
    if (!isSupportedImageFile(file)) {
        return IMAGE_TYPE_ERROR;
    }
    if (file.size > maxBytes) {
        return `Image too large. Please use an image under ${formatMegabytes(maxBytes)} MB.`;
    }
    if (!(await canDecodeImage(file))) {
        return options.invalidImageMessage ?? 'This doesn\'t appear to be a valid image. Please try a different file.';
    }
    return null;
}
